import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { fetchAcademicDashboardData } from '../../../store/actions/academicDashboardActions'
import ArrowIcon from '../../../components/elements/icons/ArrowIcon'


const AcademicsDashboard = () => {
  const dispatch = useDispatch()
  const academicDashboardSelector = useSelector((state) => state.academicDashboard)

  useEffect(() => {
    dispatch(fetchAcademicDashboardData())
  }, [dispatch])

  const toNumber = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
  }
  const toString = (value) => value == null ? '' : String(value)

  const liveData = academicDashboardSelector.academicData || {}
  const subjects = liveData.subjects || {}
  const assessments = liveData.assessments || {}
  const assessmentsOverall = assessments.overall || {}
  const topStudents = Array.isArray(assessments.topStudents) ? assessments.topStudents : []
  const classCapacity = Array.isArray(liveData.classCapacity) ? liveData.classCapacity : []
  const averageScore = toNumber(assessmentsOverall.averageScore).toFixed(1)
  const totalCapacity = classCapacity.reduce((sum, item) => sum + toNumber(item?.maxStudentCount), 0)
  const totalEnrolment = classCapacity.reduce((sum, item) => sum + toNumber(item?.currentStudents), 0)
  const utilization = totalCapacity ? Math.round((totalEnrolment / totalCapacity) * 100) : 0

  const stats = [
    { label: 'Subjects', value: toNumber(subjects.total), detail: 'Across all active classes', link: '/admin/academics/subjects', featured: true },
    { label: 'Assessment records', value: toNumber(assessmentsOverall.totalAssessments), detail: `${averageScore}% average score`, link: '/admin/academics/assessments' },
    { label: 'Class groups', value: classCapacity.length, detail: `${totalEnrolment} students enrolled`, link: '/admin/administration/classes' },
    { label: 'Capacity used', value: `${utilization}%`, detail: `${Math.max(totalCapacity - totalEnrolment, 0)} places remaining`, link: '/admin/administration/classes' },
  ]

  return (
    <div className='w-full max-w-[1600px] mx-auto'>
      <section className='mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
        <div>
          <p className='text-sm text-vcm-dark-gray'>A simple view of curriculum coverage, performance and class capacity.</p>
          {/* {usingSampleData && <p className='mt-2 text-xs text-amber-700'>Sample data is shown where live academic data is not available yet.</p>} */}
        </div>
        <Link to='/admin/academics/assessments' className='inline-flex w-fit items-center gap-2 rounded-lg bg-vcm-purple px-4 py-2.5 text-sm font-medium text-white transition hover:bg-vcm-navy'>
          View assessments <ArrowIcon className='h-4 w-4' />
        </Link>
      </section>

      <section className='mb-7 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        {stats.map((stat) => (
          <Link key={stat.label} to={stat.link} className={`group rounded-xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${stat.featured ? 'border-vcm-purple bg-vcm-purple text-white' : 'border-gray-200 bg-white text-gray-900'}`}>
            <p className={`text-sm ${stat.featured ? 'text-white/75' : 'text-vcm-dark-gray'}`}>{stat.label}</p>
            <div className='mt-4 flex items-end justify-between gap-3'>
              <p className={`font-space-grotesk text-3xl font-bold ${stat.featured ? 'text-white' : 'text-gray-900'}`}>{stat.value}</p>
              <ArrowIcon className={`h-4 w-4 transition group-hover:translate-x-1 ${stat.featured ? 'text-white' : 'text-vcm-purple'}`} />
            </div>
            <p className={`mt-2 text-xs ${stat.featured ? 'text-white/75' : 'text-vcm-dark-gray'}`}>{stat.detail}</p>
          </Link>
        ))}
      </section>

      <section className='mb-7 grid grid-cols-1 gap-5 xl:grid-cols-5'>
        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-3'>
          <div className='mb-5 flex items-center justify-between'>
            <div><p className='text-sm font-medium text-gray-900'>Assessment performance</p><p className='mt-1 text-xs text-vcm-dark-gray'>Summary across recorded assessments</p></div>
            <Link to='/admin/academics/assessments' className='text-xs font-medium text-vcm-purple hover:text-vcm-navy'>See records</Link>
          </div>
          <div className='grid grid-cols-3 divide-x divide-gray-100'>
            <div className='pr-4'><p className='text-xs text-vcm-dark-gray'>Highest score</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-emerald-600'>{toNumber(assessmentsOverall.highestScore).toFixed(1)}%</p></div>
            <div className='px-4'><p className='text-xs text-vcm-dark-gray'>Average score</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-vcm-purple'>{averageScore}%</p></div>
            <div className='pl-4'><p className='text-xs text-vcm-dark-gray'>Lowest score</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-amber-600'>{toNumber(assessmentsOverall.lowestScore).toFixed(1)}%</p></div>
          </div>
        </div>
        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2'>
          <p className='text-sm font-medium text-gray-900'>Capacity summary</p>
          <div className='mt-4 h-2 overflow-hidden rounded-full bg-gray-100'><div className='h-full rounded-full bg-vcm-purple' style={{ width: `${Math.min(utilization, 100)}%` }} /></div>
          <div className='mt-3 flex items-end justify-between'><div><p className='font-space-grotesk text-2xl font-bold text-gray-900'>{totalEnrolment}</p><p className='text-xs text-vcm-dark-gray'>enrolled students</p></div><p className='text-sm font-medium text-vcm-purple'>{utilization}% full</p></div>
          <Link to='/admin/administration/classes' className='mt-4 inline-flex items-center gap-1 text-xs font-medium text-vcm-purple hover:text-vcm-navy'>Manage classes <ArrowIcon className='h-3 w-3' /></Link>
        </div>
      </section>

      <section className='grid grid-cols-1 gap-5 xl:grid-cols-5'>
        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2'>
          <div className='mb-4 flex items-center justify-between'><div><p className='text-sm font-medium text-gray-900'>Top students</p><p className='mt-1 text-xs text-vcm-dark-gray'>By average assessment score</p></div></div>
          <div className='space-y-1'>
            {topStudents.slice(0, 4).map((student, index) => {
              const studentData = student || {}
              return <div key={`${toString(studentData.admissionNumber)}-${index}`} className='flex items-center justify-between rounded-lg px-2 py-3 hover:bg-gray-50'>
              <div className='flex min-w-0 items-center gap-3'><span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-vcm-light-purple text-xs font-bold text-vcm-purple'>{index + 1}</span><div className='min-w-0'><p className='truncate text-sm font-medium text-gray-900'>{toString(studentData.studentName)}</p><p className='text-xs text-vcm-dark-gray'>{toString(studentData.admissionNumber)}</p></div></div>
              <p className='font-space-grotesk text-sm font-bold text-vcm-purple'>{toNumber(studentData.averageScore).toFixed(1)}%</p>
            </div>
            })}
          </div>
        </div>
        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-3'>
          <div className='mb-4 flex items-center justify-between'><div><p className='text-sm font-medium text-gray-900'>Class capacity</p><p className='mt-1 text-xs text-vcm-dark-gray'>Prioritise groups that are close to full</p></div><Link to='/admin/administration/classes' className='text-xs font-medium text-vcm-purple hover:text-vcm-navy'>Manage classes</Link></div>
          <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
            {classCapacity.slice(0, 4).map((item, index) => {
              const classData = item || {}
              const maxStudentCount = toNumber(classData.maxStudentCount)
              const currentStudents = toNumber(classData.currentStudents)
              const rate = toNumber(classData.utilizationRate ?? (maxStudentCount ? (currentStudents / maxStudentCount) * 100 : 0))
              const tone = rate >= 80 ? 'bg-rose-500' : rate >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
              return <div key={`${toString(classData.className)}-${toString(classData.label)}-${index}`} className='rounded-lg border border-gray-100 p-4'>
                <div className='flex items-start justify-between gap-3'><div><p className='text-sm font-medium text-gray-900'>{toString(classData.className)}{classData.label ? ` · ${toString(classData.label)}` : ''}</p><p className='mt-1 text-xs text-vcm-dark-gray'>{currentStudents} of {maxStudentCount} students</p></div><span className='text-sm font-medium text-gray-700'>{Math.round(rate)}%</span></div>
                <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100'><div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(rate, 100)}%` }} /></div>
              </div>
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

export default AcademicsDashboard
